import React, { useState, useEffect, use } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';
import Skeleton from '@mui/material/Skeleton';
import IconButton from '@mui/material/IconButton';
import TranslateIcon from '@mui/icons-material/Translate';
import LanguageIcon from '@mui/icons-material/Language';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import { setSelectedLanguage } from '@modules/chapter';

import ChapLangStateSwitch from './ChapLangStateSwitch';
import AddChapterModal from './AddChapterModal';
import DeleteChapterModal from './DeleteChapterModal';

const ChapterHeader = ({
    isAddModalOpen,
    setIsAddModalOpen,
    currentSelectedLanguage,
    setCurrentSelectedLanguage,
    availableChapterLanguages,
    setAvailableChapterLanguages,
    isLoading = false,
    setShowNoChap
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const [chapterLanguageStates, setChapterLanguageStates] = useState({});
    const [anchorEl, setAnchorEl] = useState(null);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [languageToDelete, setLanguageToDelete] = useState(null);

    useEffect(() => {
        setIsPopoverOpen(false);
    }, [location.pathname]);

    // 사용 가능한 자막 언어 목록이 변경될 때마다 언어 상태 초기화
    useEffect(() => {
        if (availableChapterLanguages && availableChapterLanguages.length > 0) {
            const initialStates = {};
            availableChapterLanguages.forEach(lang => {
                initialStates[lang.lang] = lang.state === 1;
            });
            setChapterLanguageStates(initialStates);

            // 현재 선택된 언어가 있다면, 갱신된 목록에서 동일한 코드를 가진 언어로 업데이트
            if (currentSelectedLanguage) {
                const updatedCurrentLang = availableChapterLanguages.find(
                    lang => lang.lang === currentSelectedLanguage.lang
                );
                if (updatedCurrentLang) {
                    setCurrentSelectedLanguage(updatedCurrentLang);
                }
            }
        }
        else if (availableChapterLanguages.length === 0 && currentSelectedLanguage) {
            // 빈 배열일 때만 currentSelectedLanguage를 초기화
            setCurrentSelectedLanguage(null);
            const pathSegments = location.pathname.split('/');
            const chapterIndex = pathSegments.findIndex(segment => segment === 'chapter');

            if (chapterIndex !== -1) {
                const newPath = pathSegments.slice(0, chapterIndex + 1).join('/');
                navigate(newPath, { replace: true });
            }
        }
    }, [availableChapterLanguages]);

    // 언어 선택 시 현재 선택된 언어 업데이트
    const handleLanguageChange = (lang) => {
        const selectedLangObject = availableChapterLanguages.find(subLang => subLang.lang === lang);

        if (selectedLangObject) {
            setCurrentSelectedLanguage(selectedLangObject);
            dispatch(setSelectedLanguage(selectedLangObject));
            const pathSegments = location.pathname.split('/');
            const chapterIndex = pathSegments.findIndex(segment => segment === 'chapter');

            if (chapterIndex !== -1) {
                const newPath = pathSegments.slice(0, chapterIndex + 1).join('/') + '/' + lang;
                console.log("newPath", newPath);
                navigate(newPath, { replace: true });
            }
        }
    };

    const handleDeleteClick = (lang) => {
        setLanguageToDelete(lang);
        setDeleteDialogOpen(true);
    };

    return (
        <>
            <div className='box-language-selector'>
                <div className='box-chapEdit-btns'>
                    {isLoading ? (
                        <>
                            <Skeleton variant="rectangular" width={180} height={36} className="skeleton-chap-btn" />
                            <Skeleton variant="rectangular" width={180} height={36} className="skeleton-chap-btn" />
                        </>
                    ) : (
                        <>
                            <Button
                                className="chapEditBtn"
                                variant="contained"
                                startIcon={<AddIcon />}
                                color="primary"
                                onClick={() => setIsAddModalOpen(true)}
                            >
                                {t("vod.manage.chapter_tab.create_other_language_chapter")}
                            </Button>

                            <Button
                                className="chapSetStateBtn"
                                variant="outlined"
                                onClick={(e) => { setAnchorEl(e.currentTarget); setIsPopoverOpen(!isPopoverOpen) }}
                            >
                                <Box className="container-chap-btn-content">
                                    <LanguageIcon className="icon-chap-language" />
                                    <Typography className="text-chap-state">
                                        {t('vod.manage.usage_setting')}
                                    </Typography>
                                </Box>
                                <Box className="container-chap-chip">
                                    <Chip
                                        label={`${t('vod.manage.chapter_tab.language')} ${currentSelectedLanguage?.state ? t('vod.manage.use') : t('vod.manage.un_use')}`}
                                        size="small"
                                        className={`chip-chap-state ${currentSelectedLanguage?.state ? 'chip-chap-active' : 'chip-chap-inactive'}`}
                                    />
                                    <KeyboardArrowDown className="icon-chap-arrow" />
                                </Box>
                            </Button>

                            <Popover
                                open={isPopoverOpen}
                                anchorEl={anchorEl}
                                onClose={() => setIsPopoverOpen(false)}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'center',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center',
                                }}
                                disablePortal
                            >
                                {
                                    currentSelectedLanguage ? (
                                        <Box className="container-chap-popover">
                                            <Typography variant="h6" gutterBottom className="text-chap-popover-title">
                                                {t('vod.manage.current_language_settings')}
                                            </Typography>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box display="flex" alignItems="center" gap={2}>
                                                    <LanguageIcon className="h-4 w-4 text-muted-foreground" />
                                                    <span style={{ fontSize: '12px' }}>{t(`languages.${currentSelectedLanguage.lang}`)}</span>
                                                </Box>
                                                <ChapLangStateSwitch
                                                    language={currentSelectedLanguage}
                                                    setAvailableChapterLanguages={setAvailableChapterLanguages}
                                                    setChapterLanguageStates={setChapterLanguageStates}
                                                    setCurrentSelectedLanguage={setCurrentSelectedLanguage}
                                                />
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteClick(currentSelectedLanguage)}
                                                    className="btn-chap-delete"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </div>

                                            {availableChapterLanguages.length > 1 && (
                                                <>
                                                    <Typography variant="h6" className="text-chap-popover-subtitle">
                                                        {t('vod.manage.other_language_settings')}
                                                    </Typography>
                                                    {availableChapterLanguages
                                                        .filter(lang => lang.lang !== currentSelectedLanguage.lang)
                                                        .map((lang, idx) => (
                                                            <Box
                                                                key={idx}
                                                                display="flex"
                                                                alignItems="center"
                                                                justifyContent="space-between"
                                                                mt={1}
                                                                className="container-chap-lang-item"
                                                            >
                                                                <Typography className="text-chap-lang-name">{t(`languages.${lang.lang}`)}</Typography>

                                                                <Box display="flex" alignItems="center" className="container-chap-lang-actions">
                                                                    <Chip
                                                                        label={chapterLanguageStates[lang.lang] ? "ON" : "OFF"}
                                                                        size="small"
                                                                        className={`chip-chap-lang-state ${chapterLanguageStates[lang.lang] ? 'chip-chap-lang-active' : 'chip-chap-lang-inactive'}`}
                                                                    />

                                                                    <ChapLangStateSwitch
                                                                        language={lang}
                                                                        setAvailableChapterLanguages={setAvailableChapterLanguages}
                                                                        setChapterLanguageStates={setChapterLanguageStates}
                                                                        setCurrentSelectedLanguage={setCurrentSelectedLanguage}
                                                                    />

                                                                    <IconButton
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={() => handleDeleteClick(lang)}
                                                                        className="btn-chap-delete"
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            </Box>
                                                        ))}
                                                </>
                                            )}
                                        </Box>
                                    ) : (
                                        <Box className="container-chap-popover-empty">
                                            <Typography variant="body1">
                                                {t('vod.manage.chapter_tab.no_chapter_settings')}
                                            </Typography>
                                        </Box>
                                    )
                                }
                            </Popover>
                        </>
                    )}

                </div>

                {
                    currentSelectedLanguage && (
                        <div className="language-selector">
                            {isLoading ? (
                                <Skeleton variant="rectangular" width={180} height={56} className="skeleton-chap-selector" />
                            ) : (
                                <FormControl className="language-selector-form" variant="outlined" fullWidth>
                                    <InputLabel id="subtitle-language-label">
                                        {t('vod.manage.chapter_tab.chapter_language')}
                                    </InputLabel>
                                    <Select
                                        labelId="subtitle-language-label"
                                        id="subtitle-language-select"
                                        value={currentSelectedLanguage.lang}
                                        onChange={(event) => handleLanguageChange(event.target.value)}
                                        label={t('vod.manage.chapter_tab.chapter_language')}
                                    >
                                        {availableChapterLanguages.map((el, idx) => (
                                            <MenuItem key={idx} value={el.lang} className="item-chap-menu">
                                                {t(`languages.${el.lang}`)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </div>
                    )
                }
            </div>

            <AddChapterModal
                availableChapterLanguages={availableChapterLanguages}
                setAvailableChapterLanguages={setAvailableChapterLanguages}
                setCurrentSelectedLanguage={setCurrentSelectedLanguage}
                isAddModalOpen={isAddModalOpen}
                setIsAddModalOpen={setIsAddModalOpen}
                setShowNoChap={setShowNoChap}
            />

            <DeleteChapterModal
                setDeleteDialogOpen={setDeleteDialogOpen}
                deleteDialogOpen={deleteDialogOpen}
                languageToDelete={languageToDelete}
                setLanguageToDelete={setLanguageToDelete}
                availableChapterLanguages={availableChapterLanguages}
                setAvailableChapterLanguages={setAvailableChapterLanguages}
                currentSelectedLanguage={currentSelectedLanguage}
                setCurrentSelectedLanguage={setCurrentSelectedLanguage}
                setShowNoChap={setShowNoChap}
            />
        </>

    );
};

export default ChapterHeader;
